import { httpFetchPaymentMerchants } from "api/admin/stats";
import AdminPermission from "components/admin/AdminPermission";
import AdminSidebar from "components/layout/AdminSidebar";
import Container from "components/layout/Container";
import Main from "components/layout/Main";
import { Select } from "components/misc";
import VButton from "components/ui/VButton";
import useAdminAccess from "hooks/useAdminAccess";
import useAsync from "hooks/useAsync";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import Toggle, { ToggleProps } from "react-toggle";
import "react-toggle/style.css";
import { showSuccess } from "utilities/misc";
import { post } from "utilities/network";
import PaymentRoutes from "./PaymentRoutes";

/** Must match admin API `BILL_TYPES` / `billType` body validation */
type BillTypeKey =
  | "airtime"
  | "mobile_data"
  | "cable"
  | "electricity"
  | "betting";

const BILL_TYPE_ROWS: { key: BillTypeKey; label: string }[] = [
  { key: "airtime", label: "Airtime" },
  { key: "mobile_data", label: "Mobile data" },
  { key: "cable", label: "Cable" },
  { key: "electricity", label: "Electricity" },
  { key: "betting", label: "Betting" },
];

const PaymentMerchant: React.FC = () => {
  const { can } = useAdminAccess();
  const [activeMerchants, setActiveMerchants] = useState<any>([]);

  const { data, refetch } = useQuery(
    "paymentMerchants",
    httpFetchPaymentMerchants,
    {
      initialData: {
        merchants: [],
        paymentMerchants: [],
        nairaWithdrawal: true,
        billsPayment: true,
        billsProviders: [],
        currentBillProvider: "",
        currentBillProviders: {} as Partial<Record<BillTypeKey, string>>,
        otpProviders: [],
        currentOtpProvider: "",
      },
      onSuccess: (data) => {
        setActiveMerchants(data.paymentMerchants);
      },
    },
  );

  const {
    execute: updateMerchant,
    status,
    value,
  } = useAsync(
    () =>
      post(`admin/transactions/setPaymentMerchant`, {
        paymentMerchant: activeMerchants,
      }),
    false,
  );

  const handleSubmit = () => {
    updateMerchant();
  };

  useEffect(() => {
    if (value?.data?.success) {
      showSuccess(value?.data?.message || "Updated successfully");
    }
  }, [value]);

  const handleChange = (e: any, index: number) => {
    const newMerchants = [...activeMerchants];
    const { value, name } = e.target;
    newMerchants[index][name] =
      name === "merchant" ? getMerchantByName(value)?.key : Number(value);
    setActiveMerchants(newMerchants);
  };

  const getMerchantByKey = (key: string) =>
    data?.merchants.find((a: any) => key === a.key);
  const getMerchantByName = (name: string) =>
    data?.merchants.find((a: any) => name === a.name);

  const handleToggleWithdrawal = (
    props: ToggleProps & { target: { checked: boolean } },
  ) => {
    post(`admin/transactions/setNairaWithdrawal`, {
      nairaWithdrawal: props.target.checked,
    })
      .then((res) => {
        if (res?.data?.success) {
          showSuccess(res?.data?.message || "Updated successfully");
        }
        refetch();
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const handleToggleBillsPayment = (
    props: ToggleProps & { target: { checked: boolean } },
  ) => {
    post(`admin/transactions/setBillsPayment`, {
      billsPayment: props.target.checked,
    })
      .then((res) => {
        if (res?.data?.success) {
          showSuccess(res?.data?.message || "Updated successfully");
        }
        refetch();
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const setBillsPaymentProvider = (
    provider: string,
    billType?: BillTypeKey,
  ) => {
    const body = billType ? { provider, billType } : { provider };
    post(`admin/transactions/setBillsPaymentProvider`, body)
      .then((res) => {
        if (res?.data?.success) {
          showSuccess(res?.data?.message || "Updated successfully");
        }
        refetch();
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const handleChangeDefaultBillProvider = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const provider = (e.target.value || "").toLowerCase();
    setBillsPaymentProvider(provider);
  };

  const handleChangeBillProviderForType =
    (billType: BillTypeKey) => (e: React.ChangeEvent<HTMLSelectElement>) => {
      const provider = (e.target.value || "").toLowerCase();
      setBillsPaymentProvider(provider, billType);
    };

  const handleChangeOtpProvider = (e: any) => {
    const provider = (e.target.value || "")?.toLowerCase();
    post(`admin/transactions/setOtpProvider`, {
      provider,
    })
      .then((res) => {
        if (res?.data?.success) {
          showSuccess(res?.data?.message || "Updated successfully");
        }
        refetch();
      })
      .catch((e) => {
        console.log(e);
      });
  };

  return (
    <Container>
      <AdminSidebar />
      <Main className="mx-5 mt-5 text-left">
        <div className="mb-10">
          <h2 className="text-xl">Payment providers</h2>
          <PaymentRoutes />
          <h3 className="text-lg">Legacy Nigeria payout amount rules</h3>
          <div className="mt-6">
            <AdminPermission permission={"transactions.nairaWithdrawal.edit"}>
              <label className="flex items-center gap-x-4">
                <AdminPermission
                  permission={"transactions.nairaWithdrawal.edit"}
                >
                  <Toggle
                    checked={data?.nairaWithdrawal}
                    onChange={handleToggleWithdrawal}
                  />
                </AdminPermission>
                <span>Enable Withdrawal</span>
              </label>
            </AdminPermission>
          </div>
          <div className="flex gap-8 mt-10">
            <div className="flex">
              <div className="flex flex-col gap-4">
                {data?.paymentMerchants?.map((a: any, index: number) => (
                  <div
                    key={`${a?.merchant}-${index}`}
                    className="relative flex items-center gap-x-5"
                  >
                    <div>
                      <label>Merchant {index + 1}</label>
                      <AdminPermission
                        permission={"transactions.paymentMerchant.edit"}
                      >
                        <Select
                          name="merchant"
                          onChange={(e) => handleChange(e, index)}
                          data={data?.merchants.map((p: any) => p.name)}
                          value={
                            getMerchantByKey(activeMerchants[index]?.merchant)
                              ?.name
                          }
                        />
                      </AdminPermission>
                    </div>
                    <span> {"=>"} </span>
                    <div className="flex items-center mb-5 gap-x-3">
                      <div>
                        <label className="mb-2">Min. Amount</label>
                        <input
                          type="number"
                          name="min"
                          disabled={!can("transactions.paymentMerchant.edit")}
                          value={activeMerchants[index]?.min}
                          onChange={(e) => handleChange(e, index)}
                          className="w-full py-2 border-gray-300 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="mb-2">Max. Amount</label>
                        <input
                          type="number"
                          name="max"
                          disabled={!can("transactions.paymentMerchant.edit")}
                          value={activeMerchants[index]?.max}
                          onChange={(e) => handleChange(e, index)}
                          className="w-full py-2 border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <AdminPermission
                  permission={"transactions.paymentMerchant.edit"}
                >
                  <VButton
                    status={status}
                    onClick={handleSubmit}
                    className="w-5/12 px-20 py-3 font-light text-white bg-black rounded-lg"
                  >
                    Save
                  </VButton>
                </AdminPermission>
              </div>
            </div>
          </div>
        </div>
        <AdminPermission permission={"transactions.billsProvider.edit"}>
          <div className="mb-10">
            <h2 className="text-xl">Bill Payment Provider</h2>
            <div className="mt-6">
              <label className="flex items-center gap-x-4">
                <AdminPermission permission={"transactions.billsProvider.edit"}>
                  <Toggle
                    checked={data?.billsPayment}
                    onChange={handleToggleBillsPayment}
                  />
                </AdminPermission>
                <span>Enable Bill Purchase</span>
              </label>
            </div>
            <div className="flex flex-col max-w-4xl gap-3 mt-10">
              <div>
                <p className="mb-2 text-sm text-gray-600">
                  Sets the legacy global key and every bill type to the same
                  provider.
                </p>
                <label>Default provider (all bill types)</label>
                <AdminPermission permission={"transactions.billsProvider.edit"}>
                  <Select
                    className="capitalize"
                    name="billProviderDefault"
                    onChange={handleChangeDefaultBillProvider}
                    data={data?.billsProviders}
                    value={data?.currentBillProvider ?? ""}
                  />
                </AdminPermission>
              </div>
              <div>
                <h3 className="mb-4 text-base font-medium text-gray-800">
                  Provider by bill type
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 md:gap-x-8">
                  {BILL_TYPE_ROWS.map(({ key, label }) => (
                    <div key={key}>
                      <label className="block mb-2 text-sm text-gray-700">
                        {label}
                      </label>
                      <AdminPermission
                        permission={"transactions.billsProvider.edit"}
                      >
                        <Select
                          className="capitalize"
                          name={`billProvider_${key}`}
                          onChange={handleChangeBillProviderForType(key)}
                          data={data?.billsProviders}
                          value={
                            data?.currentBillProviders?.[key] ??
                            data?.currentBillProvider ??
                            ""
                          }
                        />
                      </AdminPermission>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </AdminPermission>

        <AdminPermission permission={"transactions.otpProvider.edit"}>
          <div>
            <h2 className="text-xl">OTP Provider</h2>
            <div className="flex gap-8 mt-7">
              <div className="flex">
                <div className="flex flex-col gap-4">
                  <div>
                    <label>OTP Provider</label>
                    <AdminPermission
                      permission={"transactions.otpProvider.edit"}
                    >
                      <Select
                        className="capitalize"
                        name="otpProvider"
                        onChange={(e) => handleChangeOtpProvider(e)}
                        data={data?.otpProviders}
                        value={data?.currentOtpProvider}
                      />
                    </AdminPermission>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </AdminPermission>
      </Main>
    </Container>
  );
};
export default PaymentMerchant;
